// =============================================================================
// io/sync/mergeAccounts.ts — folding one account's contents into another.
// -----------------------------------------------------------------------------
// ── 🚨 THE ORDER OF THESE FOUR STEPS IS THE WHOLE SAFETY ARGUMENT ──
//
//   1. move the loser's trades onto the keeper
//   2. delete the trades the keeper already had, identically
//   3. MOVE THE LOSER'S ADJUSTMENTS onto the keeper
//   4. delete the loser
//
// Step 3 before step 4, or the money records are gone. `account_adjustments`
// has `ON DELETE CASCADE` on `account_id` — see `deleteAccount.ts`, which exists
// mostly to count that loss before a delete can hide it — so deleting the loser
// first would destroy its eval fees, activation fees, resets and payouts. Those
// are the only record that money left the trader's bank, and the only expense
// input the Economics tab has.
//
// 🔴 THE OLD CLIENT'S MERGE DOES EXACTLY THAT, AND SAYS NOTHING ABOUT IT.
// `applyAccountMerge` (app/index.html:46452) filters the losing accounts out of
// state and saves; the adjustments go with them, and the modal's copy mentions
// only trades. On Josh's book one account carries a $99 activation fee — 32% of
// his lifetime spend — so this is not a hypothetical loss.
//
// ▶ Here they MOVE. The premise of the whole tool is that the two rows are one
// account, so its costs belong to the copy that survives.
//
// ── ⚠ NOT ONE TRANSACTION, AND THAT HAS TO BE SAID ──
//
// PostgREST gives no cross-table transaction from the browser. So each step
// reports what it completed and the caller stops at the first failure, leaving a
// state that is INCOMPLETE BUT NOT WRONG: trades moved, nothing deleted. Running
// the merge again finishes the job — every step is idempotent by construction,
// because a trade already on the keeper is not in the next plan's move list.
// =============================================================================

import type { SupabaseClient } from '@supabase/supabase-js';
import { reportFailureAs } from '../diagnostics/errorLog';
import { PAGE_ROWS } from './load';
import type { DeleteImpact } from './deleteAccount';

export interface MergeResult {
  ok: boolean;
  /** Trades moved onto the keeper. */
  moved: number;
  /** Duplicate trades removed. */
  removed: number;
  /** Fee and payout rows moved onto the keeper. */
  adjustmentsMoved: number;
  /** Accounts deleted. */
  accountsRemoved: number;
  error: string | null;
}

const EMPTY: Omit<MergeResult, 'ok' | 'error'> = {
  moved: 0, removed: 0, adjustmentsMoved: 0, accountsRemoved: 0,
};

/**
 * Move trades onto another account.
 *
 * ⚠ CHUNKED, and it stops at the first failed chunk. A half-moved merge is
 * recoverable by running it again; a silently-partial one that reported success
 * is not.
 */
async function repointTrades(
  sb: SupabaseClient, userId: string, ids: readonly string[], toAccountId: string,
): Promise<{ moved: number; error: string | null }> {
  let moved = 0;
  for (let i = 0; i < ids.length; i += PAGE_ROWS) {
    const chunk = ids.slice(i, i + PAGE_ROWS);
    const { data, error } = await sb
      .from('trades')
      .update({ account_id: toAccountId })
      .eq('user_id', userId)
      .in('id', chunk)
      .select('id');
    if (error) {
      return {
        moved,
        error: reportFailureAs(
          'move trades to the surviving account',
          error,
          `The merge stopped after moving ${moved} trade(s): ${error.message}. `
          + 'Nothing has been deleted — run the merge again to finish it.',
        ),
      };
    }
    moved += Array.isArray(data) ? data.length : 0;
  }
  return { moved, error: null };
}

/**
 * Move an account's fee and payout rows.
 *
 * ⚠ NOT SCOPED BY `user_id` — `account_adjustments` has no such column; it is
 * reached through its account, and RLS on that relationship is what protects it.
 * Checked rather than assumed: `accounts.ts:215` reads the same table with only
 * an `account_id` filter.
 */
async function repointAdjustments(
  sb: SupabaseClient, fromAccountId: string, toAccountId: string,
): Promise<{ moved: number; error: string | null }> {
  const { data, error } = await sb
    .from('account_adjustments')
    .update({ account_id: toAccountId })
    .eq('account_id', fromAccountId)
    .select('id');
  if (error) {
    return {
      moved: 0,
      error: reportFailureAs(
        'move the account’s fee records',
        error,
        `The trades were merged, but this account’s fee and payout records could `
        + `not be moved: ${error.message}. The account has NOT been deleted, so `
        + 'nothing is lost — run the merge again to finish it.',
      ),
    };
  }
  return { moved: Array.isArray(data) ? data.length : 0, error: null };
}

/**
 * Fold `dupeIds` into `keepId`.
 *
 * @param removeIds trades to delete rather than move — the keeper already has
 *   them, identically. Deleted AFTER the move, so a failure between the two
 *   leaves duplicates rather than a hole.
 */
export async function mergeAccounts(args: {
  sb: SupabaseClient;
  userId: string;
  keepId: string;
  dupeIds: readonly string[];
  repointIds: readonly string[];
  removeIds: readonly string[];
  /** Injected so the delete paths stay the ones that are already tested. */
  deleteTrades: (
    sb: SupabaseClient, userId: string, ids: readonly string[],
  ) => Promise<{ deleted: number; error: string | null }>;
  /**
   * 🚨 THE EMPTINESS CHECK, AND IT IS NOT A FORMALITY. The account is deleted
   * only after this says it holds nothing — so a trade or a fee row that did not
   * move, for any reason this code did not foresee, stops the delete instead of
   * going over the cascade with it.
   */
  countDeleteImpact: (
    sb: SupabaseClient, accountId: string,
  ) => Promise<{ impact: DeleteImpact | null; error: string | null }>;
  deleteAccount: (
    sb: SupabaseClient, impact: DeleteImpact,
  ) => Promise<{ ok: boolean; error: string | null }>;
}): Promise<MergeResult> {
  const { sb, userId, keepId, dupeIds, repointIds, removeIds } = args;

  if (!keepId || !dupeIds.length) {
    return { ...EMPTY, ok: false, error: 'Nothing to merge.' };
  }
  // 🚨 The keeper cannot also be a loser. A caller that passed both would delete
  // the account it had just moved everything onto.
  if (dupeIds.some((id) => String(id) === String(keepId))) {
    return {
      ...EMPTY, ok: false,
      error: 'Refusing to merge an account into itself.',
    };
  }

  // ── 1. the trades move ────────────────────────────────────────────────────
  const move = await repointTrades(sb, userId, repointIds, keepId);
  if (move.error) return { ...EMPTY, ok: false, moved: move.moved, error: move.error };

  // ── 2. the exact duplicates go ────────────────────────────────────────────
  let removed = 0;
  if (removeIds.length) {
    const del = await args.deleteTrades(sb, userId, removeIds);
    removed = del.deleted;
    if (del.error) {
      return { ...EMPTY, ok: false, moved: move.moved, removed, error: del.error };
    }
  }

  // ── 3. the money records move — BEFORE the account goes ───────────────────
  let adjustmentsMoved = 0;
  for (const dupeId of dupeIds) {
    const adj = await repointAdjustments(sb, String(dupeId), keepId);
    adjustmentsMoved += adj.moved;
    if (adj.error) {
      return {
        ...EMPTY, ok: false,
        moved: move.moved, removed, adjustmentsMoved, error: adj.error,
      };
    }
  }

  // ── 4. and only now the account, once it is provably empty ────────────────
  let accountsRemoved = 0;
  for (const dupeId of dupeIds) {
    const counted = await args.countDeleteImpact(sb, String(dupeId));
    if (counted.error || !counted.impact) {
      return {
        ...EMPTY, ok: false,
        moved: move.moved, removed, adjustmentsMoved, accountsRemoved,
        error: counted.error
          ?? 'Could not check what the merged account still holds, so it was '
            + 'left in place. Everything has already moved onto the keeper.',
      };
    }
    // 🚨 REFUSES rather than deleting over the cascade. "0 rows" and "we could
    // not check" look identical in a result object, and only one of them is safe
    // to act on — the same reasoning `deleteAccount` states about its own count.
    if (counted.impact.trades > 0 || counted.impact.adjustments > 0) {
      return {
        ...EMPTY, ok: false,
        moved: move.moved, removed, adjustmentsMoved, accountsRemoved,
        error: `The merged account still holds ${counted.impact.trades} trade(s) `
          + `and ${counted.impact.adjustments} fee record(s), so it was NOT `
          + 'deleted. Nothing is lost — run the merge again.',
      };
    }

    const res = await args.deleteAccount(sb, counted.impact);
    if (!res.ok) {
      return {
        ...EMPTY, ok: false,
        moved: move.moved, removed, adjustmentsMoved, accountsRemoved,
        error: res.error
          ?? 'The merged account could not be deleted. Its trades and fees have '
            + 'already moved, so nothing is lost — delete it by hand, or run the '
            + 'merge again.',
      };
    }
    accountsRemoved += 1;
  }

  return {
    ok: true,
    moved: move.moved,
    removed,
    adjustmentsMoved,
    accountsRemoved,
    error: null,
  };
}
